import { CONFIG } from 'app/config/config';
import { Injectable, OnInit } from '@angular/core';
import { Http } from '@angular/http';
import { Router, NavigationEnd, CanActivate, CanActivateChild } from '@angular/router';
import { AnalyticsService } from 'app/shared/services/analytics/analytics.service';
import { Application } from 'app/shared/services/app/application/application.model';
import { Headers } from '@angular/http';
import { AngularFire, AuthProviders, AuthMethods } from 'angularfire2';
import { SessionStub } from 'app/testing-stubs';
import { RoleType, UserApplicationProperties } from 'app/shared/services/user/user-application-properties.model';
import { ActiveUser } from '../user/active-user.model';

@Injectable()
export class Session implements CanActivate, CanActivateChild {
  // User caching variables
  public cachedUser: ActiveUser;
  private _userPromise: Promise<any>;

  private _masterAppArray: Application[] = [];
  private _masterAppHashTable: any = {};

  public dashboard: any;
  private _dashboardPromise: Promise<any>;
  public newestArticleOnDashboard: Date = new Date(null); // aka default false

  constructor(private _router: Router,
              private _http: Http,
              private _af: AngularFire,
              private _analytics: AnalyticsService) {
    setInterval(() => { this.refreshToken(); }, 60 * 60 * 1000);
  }

  get masterAppHashTable(): any {
    return this._masterAppHashTable;
  }

  getAppFromMasterHash(sysName: string) {
    return this._masterAppHashTable[sysName];
  }

  headers(token?: string): Headers {
    let headers = new Headers();
    if (token || this.cachedUser) {
      headers.append('Authorization', 'Token token=' + (token || this.cachedUser.token));
    }
    return headers;
  }

  refreshToken() {
    this.cachedUser = null;
    return this.user();
  }

  // Should only be called when logging in or refreshing the token.
  setUser(user: ActiveUser) {
    localStorage.setItem('token', user.token);
    this.cachedUser = user;
  }

  // creates application objects that will be referenced by all users that need to reference them
  // creates active user's roles and returns them (to avoid extra loops through the apps)
  initializeMasterAppObjects(jsonApps: any[]): any[] {
    let activeUserRoles: UserApplicationProperties[] = [];
    this._masterAppArray = [];
    this._masterAppHashTable = {};

    jsonApps.forEach((jsonApp) => {
      let app = new Application(jsonApp);
      this._masterAppArray.push(app);
      this._masterAppHashTable[app.sys_name] = app;
      activeUserRoles.push(new UserApplicationProperties(jsonApp.role, app));
    });

    this._masterAppArray.sort((a, b) => {
      return a.fullName.toLowerCase().localeCompare(b.fullName.toLowerCase());
    });

    return activeUserRoles;
  }

  // If the user has been loaded, simply return an observable of the user.
  // Otherwise check to see if there is a request in progress. If so, return that
  // observable.
  // Otherwise return a new observable of the user request.
  user(): Promise<ActiveUser> {
    if (this.cachedUser) {
      return Promise.resolve(this.cachedUser);
    } else if (this._userPromise) {
      return this._userPromise;
    } else {
      if (!localStorage.getItem('token')) {
        this._router.navigateByUrl('/login');
        return Promise.reject<ActiveUser>('User is not logged in.');
      }
      let user;
      let headers = this.headers(localStorage.getItem('token'));

      this._userPromise = this._http.put(CONFIG.ENDPOINT_URL + 'auth/token', null, { headers: headers })
        .toPromise()
        .then((response) => {
          user = new ActiveUser(response.json());
          user.roles = this.initializeMasterAppObjects(response.json().applications);
          user.applications = this._masterAppArray;
          user.appHash = this._masterAppHashTable;

          this.setUser(user);

          if (!user) {
            this._router.navigateByUrl('/login');
            return Promise.reject<ActiveUser>('Session has expired.');
          } else {
            this._analytics.initializeSystemAnalytics(user.id);
          }
          return this.refreshDashboard();
        })
        .then((dashboardInfo) => {
          this.newestArticleOnDashboard = this.getMostRecentArticleDate(dashboardInfo.whats_new);
          return this.signInToFirebase();
        })
        .catch((e) => {
          console.log('Error generating token: ');
          console.log(e);
          this._userPromise = null;
          localStorage.removeItem('token');
          this._router.navigateByUrl('/login');
        });
      return this._userPromise;
    }
  }

  signInToFirebase(): Promise<ActiveUser> {
    return new Promise((resolve, reject) => {
      this._af.auth.login(this.cachedUser.token, {
        provider: AuthProviders.Custom,
        method: AuthMethods.CustomToken,
      })
        .then(token => {
          setTimeout(() => {
            this._userPromise = null;
          }, 200);
          resolve(this.cachedUser);
        })
        .catch((e) => {
          console.log(e);
          reject('Error logging into firebase.');
        });
    });
  }

  get selectedAppSysName() {
    if (this._router.url.split('/')[1] === 'app')
      return this._router.url.split('/')[2];
    else return '';
  }

  checkIfIsLoggedIn() {
    if (this.user || localStorage.getItem('token')) {
      return true;
    }
    this._router.navigate(['/login']);
    return false;
  }

  kickToRoot() {
    this._router.navigate(['']);
  }

  canActivate() {
    return this.checkIfIsLoggedIn();
  }

  canActivateChild() {
    return this.checkIfIsLoggedIn();
  }

  logout() {
    this.cachedUser = null;
    this._masterAppArray = [];
    this._masterAppHashTable = {};
    this._af.auth.logout();
    this._analytics.clearAnalytics();
    let license = localStorage.getItem('FEK');
    localStorage.clear();
    localStorage.setItem('FEK', license);
  }

  refreshDashboard() {
    this.dashboard = null;
    this._dashboardPromise = null;
    return this.getDashboardInformation();
  }

  getDashboardInformation() {
    if (this.dashboard) {
      return Promise.resolve(this.dashboard);
    } else if (this._dashboardPromise) {
      return this._dashboardPromise;
    } else {
      this._dashboardPromise = this._http.get(CONFIG.ENDPOINT_URL + 'dashboard', { headers: this.headers() })
        .toPromise()
        .then((res) => {
          this.dashboard = res.json();
          this.dashboard.whats_new.forEach(el => {
            el.descriptionHTML = el.description.replace(/(?:\r\n|\r|\n)/g, '<br />');
            el.excerptHTML = el.description.substr(0, 58).replace(/(?:\r\n|\r|\n)/g, '<br />');
            el.excerptHTML = this.trimHTML(el.excerptHTML);
          });
          return this.dashboard;
        })
        .catch((e) => {
          console.log(e);
          return e.json();
        });
      return this._dashboardPromise;
    }
  }

  getMostRecentArticleDate(articles: any[]) {
    let newestDate: Date;
    articles.forEach((article) => {
      let articleDate = new Date(article.created_at);
      if (!newestDate || articleDate > newestDate) newestDate = articleDate;
    });
    return newestDate;
  }

  trimHTML(html) {
    // what's new takes up 2 lines
    if (html.split('<br />').length > 2) { // if html contains new lines,
      html = html.split('<br />')[0] + '<br />' + html.split('<br />')[1]; // we only want the first 2 lines of the html
    }
    while (this.getTextWidth(html, '12pt roboto') > 386) { // if the text is too long for the ellipses to be seen,
      html = html.substr(0, html.length - 1); // remove a character until the text and ellipses will fit
    }
    return html.trim();
  }

  getTextWidth(text, font) {
    let canvas = document.createElement('canvas');
    let context = canvas.getContext('2d');
    context.font = font;
    let metrics = context.measureText(text);
    return metrics.width;
  };
}



// WEBPACK FOOTER //
// /app/src/app/shared/services/session/session.service.ts